<template>
  <div style="padding: 16px; background: #F8F8F9;">
    <div class="cm-panel">
      <div class="cm-panel-head">
        <div class="cm-panel-title">Team Caseloads</div>
        <div class="cm-panel-head-actions">
          <span style="display: flex; align-items: center; gap: 8px;">
            <span class="cm-eyebrow">Program</span>
            <select class="cm-select cm-select-sm" v-model="caseloadProgram" @change="fetchCaseloads">
              <option value="">All</option>
              <option value="61">TOC</option>
              <option value="60">Vital Care+</option>
              <option value="743">VC+ TOC</option>
              <option value="59">CDM</option>
              <option value="746">SNF</option>
              <option value="747">Direct Referral</option>
            </select>
          </span>
          <button class="cm-icon-btn" @click="fetchCaseloads" title="Refresh">
            <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :spin="loading" style="font-size: 11px;" />
          </button>
        </div>
      </div>

      <!-- Summary stat chips -->
      <div v-if="caseloadSummary" style="display: flex; align-items: center; gap: 8px; padding: 12px 16px; border-bottom: 1px solid #F0F0F1;">
        <div class="cm-stat"><span class="cm-stat-label">Active Tasks</span><span class="cm-stat-value">{{ caseloadSummary.total_active_tasks }}</span></div>
        <div class="cm-stat is-warn"><span class="cm-stat-label">Unassigned</span><span class="cm-stat-value">{{ caseloadSummary.unassigned_tasks }}</span></div>
        <div class="cm-stat"><span class="cm-stat-label">Members</span><span class="cm-stat-value">{{ caseloadSummary.total_assigned_members }}</span></div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="cm-loading"><div class="cm-spinner"></div></div>

      <!-- Empty -->
      <div v-else-if="members.length === 0" class="cm-empty">
        <FontAwesomeIcon :icon="['fas', 'users']" class="ico" />
        <div class="msg">No team assignments yet</div>
      </div>

      <!-- Table -->
      <div v-else>
        <table class="cm-table">
          <thead>
            <tr>
              <th>Team Member</th>
              <th>Role</th>
              <th class="num">Active</th>
              <th class="num">Snoozed</th>
              <th class="num">Overdue</th>
              <th>Programs</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="member in members" :key="member.member_id">
              <td>
                <span style="display: inline-flex; align-items: center; gap: 8px;">
                  <span class="cm-avatar sensitive" :style="avatarStyle(member.member_name)">{{ avatarInitials(member.member_name) }}</span>
                  <span class="strong sensitive">{{ formatName(member.member_name) }}</span>
                </span>
              </td>
              <td>{{ member.role || '—' }}</td>
              <td class="num cm-tnum"><span class="strong">{{ member.active }}</span></td>
              <td class="num cm-tnum">{{ member.snoozed }}</td>
              <td class="num cm-tnum">
                <span v-if="member.overdue > 0" style="color: #D7402B; font-weight: 700;">{{ member.overdue }}</span>
                <span v-else class="dim">0</span>
              </td>
              <td>
                <span style="display: flex; gap: 4px; flex-wrap: wrap;">
                  <span v-for="prog in member.programs" :key="prog.metric_name" class="cm-badge"
                    :style="{ background: progColor(prog.metric_name) + '1A', color: progTextColor(prog.metric_name) }">
                    {{ progShortName(prog.metric_name) }}&nbsp;<span style="font-weight: 600; opacity: 0.75;">{{ prog.count }}</span>
                  </span>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const PROG_COLORS = {
  'Transitions of Care': '#DC2626', 'Vytal Care Plus': '#7C3AED',
  'Chronic Disease Management': '#6366F1', 'SNF': '#EC4899',
  'Direct Referral': '#14B8A6', 'LCSW': '#F97316',
};
const PROG_SHORT = {
  'Transitions of Care': 'TOC', 'Vytal Care Plus': 'VC+',
  'Chronic Disease Management': 'CDM',
};

export default {
  name: 'CaseloadPanel',
  components: { FontAwesomeIcon },
  data() {
    return { loading: false, members: [], caseloadSummary: null, caseloadProgram: '' };
  },
  methods: {
    async fetchCaseloads() {
      this.loading = true;
      try {
        const params = {};
        if (this.caseloadProgram) params.metric_ids = this.caseloadProgram;
        const { data } = await axios.get('/api/care-management/caseloads/', { params });
        this.members = data.members || [];
        this.caseloadSummary = data.summary || null;
      } catch (e) { console.error('Error fetching caseloads:', e); }
      finally { this.loading = false; }
    },
    formatName(name) {
      if (!name) return '—';
      if (name.includes(',')) {
        const [left, right] = name.split(',').map(s => s.trim());
        if (left.includes(' ') || /^(RN|NP|MD|DO|PA|LPN|LCSW|MSW|BSN|MSN|PHARMD|FNP|APRN|CCM|CMA|MA)$/i.test(right)) return name;
        return `${this.cap(right)} ${this.cap(left)}`;
      }
      return name;
    },
    cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : ''; },
    avatarInitials(name) {
      const parts = this.formatName(name).split(' ');
      return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
    },
    avatarStyle(name) {
      if (!name) return {};
      const hue = [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
      return { color: `hsl(${hue} 42% 34%)`, background: `hsl(${hue} 44% 92%)` };
    },
    progShortName(name) { return PROG_SHORT[name] || name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 4) || '?'; },
    progColor(name) { return PROG_COLORS[name] || '#6B7280'; },
    progTextColor(name) {
      const hex = this.progColor(name);
      // Darken for text readability
      const n = parseInt(hex.slice(1), 16);
      let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
      r = Math.round(r * 0.68); g = Math.round(g * 0.68); b = Math.round(b * 0.68);
      const cl = v => Math.max(0, Math.min(255, v));
      return '#' + [cl(r), cl(g), cl(b)].map(v => v.toString(16).padStart(2, '0')).join('');
    },
  },
  mounted() { this.fetchCaseloads(); },
};
</script>
